import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  useSelfAllocations,
  useSelfAllocationEligibility,
  useCreateSelfAllocation,
} from "@/features/finances/hooks/useSelfAllocation";
import type {
  SelfAllocationRequest,
  SelfAllocationStatus,
} from "@/features/finances/types/selfAllocation";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { BackToFridayLink } from "@/features/tasks/components/BackToFridayLink";

const MONTHS_PL = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

const STATUS_PILL: Record<
  SelfAllocationStatus,
  { label: string; klass: string }
> = {
  draft: { label: "Szkic", klass: "bg-gray-100 text-gray-700" },
  submitted: { label: "Złożona", klass: "bg-blue-100 text-blue-800" },
  approved: { label: "Zatwierdzona", klass: "bg-green-100 text-green-800" },
  rejected: { label: "Odrzucona", klass: "bg-red-100 text-red-800" },
};

function formatPeriod(year: number, month: number) {
  return `${MONTHS_PL[month - 1]} ${year}`;
}

export default function SelfAllocationListPage() {
  const navigate = useNavigate();
  // Scope to the current user — without this, elevated roles (CEO, technical
  // director) see other employees' requests under "Twoje wnioski".
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: list, isLoading } = useSelfAllocations(
    currentUserId ? { employee: currentUserId } : undefined,
  );
  const { data: eligibility } = useSelfAllocationEligibility();
  const createMut = useCreateSelfAllocation();

  const items: SelfAllocationRequest[] = list?.data ?? [];

  const periodsWithoutRequest = useMemo(
    () => (eligibility?.periods ?? []).filter((p) => !p.has_request),
    [eligibility],
  );

  const handleCreate = async (year: number, month: number) => {
    const created = await createMut.mutateAsync({ year, month });
    navigate(`/finances/self-allocation/${created.id}`);
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Widok wywołany z sekcji „Postęp prac" w Friday — droga powrotna. */}
      <BackToFridayLink className="mb-2" />
      <h1 className="text-2xl font-semibold mb-1">
        Rozkład pracy nad przedsięwzięciami
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Oszacuj, jaką część swojego czasu pracy w bieżącym lub poprzednim
        miesiącu poświęciłeś na każde z aktywnych przedsięwzięć (używane do
        oszacowania miesięcznych nakładów realizacyjnych GK ELEKTIM
        per-przedsięwzięcie).
      </p>

      {/* Nowe okresy do złożenia */}
      {periodsWithoutRequest.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded p-4 mb-6">
          <h2 className="font-medium text-amber-900 mb-3">Do uzupełnienia</h2>
          <ul className="space-y-2">
            {periodsWithoutRequest.map((p) => (
              <li
                key={`${p.year}-${p.month}`}
                className="flex items-center justify-between"
              >
                <span className="text-sm">
                  Okres: <strong>{formatPeriod(p.year, p.month)}</strong>
                  {!p.submittable && (
                    <span className="block text-xs text-amber-700">
                      Bieżący miesiąc — uzupełniaj szkic na bieżąco, złożysz go od
                      ostatniego dnia miesiąca.
                    </span>
                  )}
                </span>
                <Button
                  size="sm"
                  loading={createMut.isPending}
                  onClick={() => handleCreate(p.year, p.month)}
                  data-testid={`btn-new-${p.year}-${p.month}`}
                >
                  {p.submittable ? "Nowy wniosek" : "Rozpocznij szkic"}
                </Button>
              </li>
            ))}
          </ul>
          {eligibility && (
            <p className="text-xs text-amber-700 mt-3">
              Wnioski za poprzednie miesiące należy złożyć do{" "}
              {eligibility.deadline_day}. dnia kolejnego miesiąca.
            </p>
          )}
        </div>
      )}

      {/* Lista wniosków */}
      <h2 className="font-medium mb-3">Twoje wnioski</h2>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">Brak wniosków.</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500">
                  Okres
                </th>
                <th className="text-center px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500">
                  Status
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500">
                  Suma %
                </th>
                <th className="px-4 py-2 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => {
                const pill = STATUS_PILL[it.status];
                const total = parseFloat(it.lines_total_fraction || "0");
                return (
                  <tr key={it.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {formatPeriod(it.year, it.month)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        data-testid={`status-${it.id}`}
                        className={`inline-block px-2 py-0.5 text-xs rounded ${pill.klass}`}
                      >
                        {pill.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {Math.round(total * 100)}%
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/finances/self-allocation/${it.id}`}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Otwórz
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
